import React, { useEffect, useState } from "react";
import { PotentialMatrixFilterKeyEnum } from "../../../types/api/filters.type";
import { PotentialMatrixFilters } from "../../../hooks/usePotentialMatrixFilters.hook";
import { DevelopmentChart } from "./DevelopmentChart/DevelopmentChart";
import {
  InternalGradeStat,
  MatrixDevelopmentItem,
} from "../../../types/api/potentialMatrix.type";
import {
  AlignmentEnum,
  CollaboratorInfo,
  FilterProps,
  FontWeightEnum,
} from "../../../types/AppTypes";
import {
  MatrixGrid,
  MatrixCalibrationProps,
} from "../../../components/MatrixGrid/MatrixGrid";
import { DevelopmentMatrixDetails } from "../DevelopmentMatrixDetails/DevelopmentMatrixDetails";
import {
  useMatrixDevelopmentCollaborators,
  usePotentialMatrixDevelopment,
  usePotentialMatrixDevelopmentChartStats,
} from "../../../hooks/potentialMatrix/usePotentialMatrixDevelopment.hook";
import { PotentialMatrixTabLayout } from "../PotentialMatrixTabLayout/PotentialMatrixTabLayout";
import {
  ALL_COLLABORATORS_LABEL,
  COMPETENCE_CENTER_LABEL,
  DISTRIBUTION_CHART_TITLE_LABEL,
  MATRIX_DEVELOPMENT_DOWNLOAD_TITLE_LABEL,
  PERCENTAGE_VIEW_LABEL,
  PRACTICE_LABEL,
  START_DEVELOPMENT_COMMITTEE_LABEL,
  CONFIRM_CALIBRATION_FINAL_MESSAGE,
  CONFIRM_CALIBRATION_FINAL,
} from "../../../constants/Dictionary";
import { CalibrationModal } from "../../../components/CalibrationModal/CalibrationModal";
import { PopupConfirm } from "../../../components/PopupConfirm/PopupConfirm";
import { ResumePanel } from "../../../components/ResumePanel/ResumePanel";
import MatrixForDownload from "../../PotentialMatrix/MatrixForDownload/MatrixForDownload";
import { BodyTyp } from "../../../components/Typography/Typography";
import ToggleSwitch from "../../../components/core/ToggleSwitch/ToggleSwitch";
import { DoubleStructure } from "../../../components/core/DoubleStructure/DoubleStructure";
import { TypeMatrixEnum } from "../../../constants/PotentialMatrixConstants";
import { useAppSelector } from "../../../hooks/store.hook";
import {
  MatrixSizes,
  MatrixViewModeV3,
} from "../../../constants/PotentialMatrixConstants";
import { useMatrixDownloadSubtitle } from "../../../hooks/potentialMatrix/useMatrixDownloadSubtitle.hook";

type DevelopmentMatrixProps = {
  filters: PotentialMatrixFilters;
  createQueryParams: () => FilterProps[];
  handleFilters: (
    key: PotentialMatrixFilterKeyEnum,
    value: PotentialMatrixFilters[PotentialMatrixFilterKeyEnum],
  ) => void;
  calibration?: MatrixCalibrationProps;
  showDetailsChip?: boolean;
  previousYear?: number;
  showDownloadPreview?: boolean;
  selectedViewMode: MatrixViewModeV3;
  setSelectedViewMode: (mode: MatrixViewModeV3) => void;
  isDownloadEnabled?: boolean;
  downloadRef?: React.Ref<HTMLDivElement>;
  downloadData?: MatrixDevelopmentItem[];
  downloadInternalGradeStats?: InternalGradeStat[];
  onDetailsViewChange?: (isOpen: boolean) => void;
};

export const DevelopmentMatrix = ({
  filters,
  createQueryParams,
  handleFilters,
  calibration,
  showDetailsChip,
  previousYear,
  showDownloadPreview = false,
  selectedViewMode,
  setSelectedViewMode,
  isDownloadEnabled = false,
  downloadRef,
  downloadData,
  downloadInternalGradeStats,
  onDetailsViewChange,
}: DevelopmentMatrixProps) => {
  const { config } = useAppSelector((state) => state.config);
  const [showCalibrationModal, setShowCalibrationModal] = useState(false);
  const [isOpenConfirmCalibrationModal, setIsOpenConfirmCalibrationModal] =
    useState(false);
  const [showResume, setShowResume] = useState(false);
  const [collaboratorId, setCollaboratorId] = useState<number | null>(null);
  const [itemDetails, setItemDetails] = useState<MatrixDevelopmentItem | null>(
    null,
  );
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [isAnimatingClose, setIsAnimatingClose] = useState(false);
  const [isAnimatingOpen, setIsAnimatingOpen] = useState(false);
  const [showPercentage, setShowPercentage] = useState(false);

  const { practiceLabel, ccValue } = useMatrixDownloadSubtitle({ filters });

  const { data, internalGradeStats, isFetching } =
    usePotentialMatrixDevelopment({
      filters,
      createQueryParams,
      enabled: itemDetails === null,
    });

  const {
    data: potentialMatrixStatsData,
    isFetching: loadingPotentialMatrixStats,
  } = usePotentialMatrixDevelopmentChartStats({
    filters,
    enabled: true,
  });

  const hasData =
    potentialMatrixStatsData?.some((row) => row.internalGrades.length > 0) ??
    false;

  const {
    data: matrixCollaborators,
    initLoading: matrixCollaboratorsLoading,
    canLoadMore: canLoadMoreCollaborators,
    fetchMore: fetchMoreCollaborators,
    fetchingMore: fetchingMoreCollaborators,
    clearSearch: clearMatrixCollaboratorsSearch,
    setSearch: setMatrixCollaboratorsSearchValue,
  } = useMatrixDevelopmentCollaborators({
    filters,
    enabled: showSearch && searchValue !== "",
  });

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setMatrixCollaboratorsSearchValue(value);

    if (filters.collaboratorId) {
      handleFilters(PotentialMatrixFilterKeyEnum.COLLABORATOR_ID, null);
    }
  };

  const handleSelectCollaborator = (
    collaboratorId: number,
    collaboratorLabel: string,
  ) => {
    setSearchValue(collaboratorLabel);
    setMatrixCollaboratorsSearchValue(collaboratorLabel);
    handleFilters(PotentialMatrixFilterKeyEnum.COLLABORATOR_ID, collaboratorId);
    setShowSearch(false);
  };

  const handleClearSearch = () => {
    handleFilters(PotentialMatrixFilterKeyEnum.COLLABORATOR_ID, null);
    setSearchValue("");
    clearMatrixCollaboratorsSearch();
    setShowSearch(false);
  };

  const toggleSearchInput = () => {
    setShowSearch(false);
    if (filters.collaboratorId === null) {
      setSearchValue("");
      clearMatrixCollaboratorsSearch();
    }
  };

  const handleCloseDetails = () => {
    setIsAnimatingClose(true);

    const timeout = setTimeout(() => {
      setIsAnimatingClose(false);
      setItemDetails(null);
    }, 500);

    return () => clearTimeout(timeout);
  };

  const handleClickMatrixItem = (item: MatrixDevelopmentItem) => {
    setIsAnimatingOpen(true);
    setItemDetails(item);

    const timeout = setTimeout(() => {
      setIsAnimatingOpen(false);
    }, 500);

    return () => clearTimeout(timeout);
  };

  useEffect(() => {
    if (selectedViewMode !== MatrixViewModeV3.COLLABORATOR) {
      setShowSearch(false);
      setSearchValue("");
      clearMatrixCollaboratorsSearch();
      handleFilters(PotentialMatrixFilterKeyEnum.COLLABORATOR_ID, null);
    }
  }, [selectedViewMode]);

  useEffect(() => {
    if (!itemDetails) return;
    setItemDetails(itemDetails);
    setIsAnimatingClose(false);
  }, [itemDetails]);

  const onClickResume = (collaborator: CollaboratorInfo) => {
    setCollaboratorId(collaborator.id);
    setShowResume(true);
  };

  const yearOptions =
    config
      ?.map((item) => ({
        value: item.year,
        label: item.year.toString(),
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 2) || [];

  const currentYear = yearOptions[0]?.value;

  useEffect(() => {
    onDetailsViewChange?.(!!itemDetails);
  }, [itemDetails, onDetailsViewChange]);

  return (
    <PotentialMatrixTabLayout
      hideScroll={showResume}
      headerProps={{
        filters,
        handleFilters,
        previousYear,
        selectedViewMode,
        setSelectedViewMode,
        isIconSwitchEnabled: true,
        collaboratorSearchProps: {
          isVisible: showSearch,
          setIsVisible: setShowSearch,
          onClose: toggleSearchInput,
          value: searchValue,
          onChange: handleSearchChange,
          onClear: handleClearSearch,
          onSelectCollaborator: handleSelectCollaborator,
          data: matrixCollaborators.map((item) => item.collaborator),
          loading: matrixCollaboratorsLoading,
          fetchingMore: fetchingMoreCollaborators,
          canLoadMore: canLoadMoreCollaborators,
          fetchMore: fetchMoreCollaborators,
          showEmptyMessage: !filters.collaboratorId,
        },
        ...(selectedViewMode === MatrixViewModeV3.GLOBAL && {
          title: (
            <DoubleStructure verticalAlignment={AlignmentEnum.CENTER}>
              <BodyTyp variant="1" fontWeight={FontWeightEnum.SEMIBOLD}>
                {DISTRIBUTION_CHART_TITLE_LABEL}
              </BodyTyp>
              {hasData &&
                filters.type.value === TypeMatrixEnum.MANAGER_PROPOSAL && (
                  <div className="header-toggle">
                    <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
                      {PERCENTAGE_VIEW_LABEL}
                    </BodyTyp>
                    <ToggleSwitch
                      value={showPercentage}
                      onChange={setShowPercentage}
                    />
                  </div>
                )}
            </DoubleStructure>
          ),
        }),
        showInternalGradeFilters: false,
      }}
      footerProps={{
        onClick: () => setShowCalibrationModal(true),
        label: START_DEVELOPMENT_COMMITTEE_LABEL,
      }}
    >
      {!showDownloadPreview && selectedViewMode === MatrixViewModeV3.GLOBAL && (
        <DevelopmentChart
          potentialMatrixStatsData={potentialMatrixStatsData ?? []}
          loadingPotentialMatrixStats={loadingPotentialMatrixStats}
          showPercentage={showPercentage}
        />
      )}
      {(showDownloadPreview ||
        selectedViewMode === MatrixViewModeV3.COLLABORATOR) &&
        (itemDetails && !showDownloadPreview ? (
          <DevelopmentMatrixDetails
            item={itemDetails}
            onClose={handleCloseDetails}
            filters={filters}
            onClickResume={onClickResume}
            matrixData={data}
            isAnimatingClose={isAnimatingClose}
          />
        ) : (
          <MatrixGrid
            data={data ?? []}
            onClickItem={handleClickMatrixItem}
            size={MatrixSizes.BIG}
            selectedItem={itemDetails ?? undefined}
            loading={isFetching}
            viewMode={selectedViewMode}
            onClickResume={onClickResume}
            matrixCalibration={calibration}
            showDetailsChip={showDetailsChip}
            internalGradeStats={internalGradeStats}
            isAnimatingOpen={isAnimatingOpen}
          />
        ))}
      {isDownloadEnabled && (
        <MatrixForDownload
          ref={downloadRef}
          year={filters.year}
          title={MATRIX_DEVELOPMENT_DOWNLOAD_TITLE_LABEL}
          subtitleItems={[
            {
              label: PRACTICE_LABEL,
              value: `${practiceLabel} | ${ALL_COLLABORATORS_LABEL}`,
            },
            { label: COMPETENCE_CENTER_LABEL, value: ccValue },
          ]}
        >
          <MatrixGrid
            data={downloadData ?? []}
            internalGradeStats={downloadInternalGradeStats}
            size={MatrixSizes.BIG}
            showDownloadPreview={showDownloadPreview}
          />
        </MatrixForDownload>
      )}
      {showCalibrationModal && (
        <>
          <CalibrationModal
            isOpen
            closeModal={() => setIsOpenConfirmCalibrationModal(true)}
            year={currentYear}
          />
          <PopupConfirm
            isOpen={isOpenConfirmCalibrationModal}
            onCancel={() => setIsOpenConfirmCalibrationModal(false)}
            onConfirm={() => {
              setIsOpenConfirmCalibrationModal(false);
              setShowCalibrationModal(false);
            }}
            label={CONFIRM_CALIBRATION_FINAL}
            popupText={CONFIRM_CALIBRATION_FINAL_MESSAGE}
          />
        </>
      )}
      <ResumePanel
        collaboratorId={collaboratorId}
        isOpen={showResume}
        onClose={() => {
          setShowResume(false);
          setCollaboratorId(null);
        }}
        fullHeight
        readOnly
        hideEditButton
      />
    </PotentialMatrixTabLayout>
  );
};
